import { useId } from "react";
import { SelectControl } from "@altek/ui/components/form/Select";
import { disambiguateLabels } from "@altek/ui/lib/utils/disambiguate";
import { toast } from "@altek/ui/lib/utils/toast";
import { useScope } from "@/hooks/useScope";

/**
 * ÜST ÇUBUĞUN ŞİRKET YUVASI — ekranların okuduğu kapsamın KENDİSİ (9 Ekim 2026, kabuk turu).
 *
 * İKİ DOĞRULUK KAYNAĞI VARDI. Kabuk seçili şirketi kendi `useState`'inde ve tarayıcı belleğinde
 * tutuyordu; ekranlar ise `useScope()`'tan okuyor ve orada adres (`?company=`) kazanıyor. Seçici
 * yalnız kabuğun durumunu ve belleği değiştiriyordu: `useScope`'un hesabı adrese bağlı olduğu
 * için açık ekran ESKİ şirketin kayıtlarını göstermeyi sürdürüyordu, «Şirket değişti» bildirimi
 * çıkmışken — fark ancak başka bir ekrana geçince ortaya çıkıyordu. Adreste `?company=` olan bir
 * bağlantıyla gelen kişide seçici ile ekran iki ayrı şirketi gösteriyordu.
 *
 * Artık tek yol `useScope().setScope`: adresi yazıyor (sayfa numarasını sıfırlayarak), belleği
 * güncelliyor, ve kapsamı okuyan her sorgu anahtarı aynı anda değişiyor.
 *
 * Kural `admin-shell.md` › Header slots: birden çok şirket okuyana seçici, tek şirkette yalnız ad
 * (dar ekranda gizli), hiç yoksa hiçbir şey — tek seçenekli bir seçici, kişinin vermediği bir
 * kararı sorar. Aynı adlı iki şirket kodlarıyla ayrılıyor (`disambiguateLabels`).
 */
export function CompanySlot({
  block = false,
}: {
  /** Çekmecenin başında: satırı dolduruyor. Üst çubukta en fazla 14rem. */
  block?: boolean;
} = {}) {
  const scope = useScope();
  const id = useId();
  const entities = scope.legalEntities;

  if (entities.length > 1) {
    return (
      <>
        {/*
          ADI GÖRÜNMEYEN ETİKETTEN. Kitin `SelectControl`'ü `ariaLabel`'ı yalnız açılan listeye ve
          temizleme düğmesine yazıyor, tetiğin kendisine DEĞİL: ekran okuyucu seçiciyi adsız bir
          «açılır kutu» diye okuyordu (combobox içeriğinden ad almaz). Düğme etiketlenebilir bir
          öğe; `id` ile bağlanan gizli `<label>` ona adını veriyor. Kit isteği raporda.
        */}
        <label htmlFor={id} className="sr-only">
          Aktif şirket
        </label>
        <SelectControl
          id={id}
          ariaLabel="Aktif şirket"
          size="sm"
          className={block ? "w-full min-w-0" : "min-w-0 max-w-56"}
          value={scope.legalEntityId}
          onChange={(next) => {
            if (!next || next === scope.legalEntityId) return;
            scope.setScope(next);
            /**
             * Duyuruluyor, çünkü değişiklik başka türlü sessiz: tablo yeniden doluyor ve kişi işin
             * olup olmadığını anlayamıyor.
             */
            const entity = entities.find((candidate) => candidate.id === next);
            if (entity) toast.info("Şirket değişti", entity.name);
          }}
          options={disambiguateLabels(
            entities,
            (entity) => entity.name,
            (entity) => entity.code,
          ).map(({ item, label }) => ({ value: item.id, label }))}
        />
      </>
    );
  }

  if (entities.length === 1) {
    return (
      <span className="hidden min-w-0 truncate text-sm text-fg-muted sm:inline">
        {entities[0]?.name}
      </span>
    );
  }

  return null;
}
